/**
 * The way into the juhlanavetta: the porch, the eteinen, the wc with its
 * partitions and doors, and the doors at the crossing inside.
 */

import {
  decor,
  handle,
  solid,
  type DecorSpec,
  type HandleSpec,
  type SolidSpec,
} from '../world/Level';
import { BARN_H } from './levelDimensions';

/* -------------------------------------------------------- eteinen + wc -- */

/**
 * The eteinen is the building's front corridor, and it runs the whole depth of
 * the west end rather than stopping at the door into the juhlasali.
 *
 * That length is what the WCs need: three cubicles open off its left-hand wall
 * before anything else does, which is where a venue this shape actually puts
 * them. And the WCs are what makes the corridor a corridor: you pass all three
 * of them before the hall opens on your right, so the way in is a walk down a
 * building rather than a lobby with a door in it.
 */
export const ETEINEN = {
  /** Inner faces of the corridor. The walls are 0.35 m thick. */
  westX: 128.85,
  /** The front wall you come in through, and the dead end at the far end. */
  frontZ: -29.65,
  backZ: -39.85,
} as const;

export const WC = {
  /**
   * The cubicles start at the front wall - the first door is the first thing
   * on your left as you come through the navetan ovi.
   */
  firstZ: -30.0,
  count: 3,
  /**
   * A metre across and 2.85 m deep, which is a cubicle rather than a room.
   * They were nearly twice this wide when the turn into the hall came before
   * them; now that all three have to fit *ahead* of the turn, they are the
   * width a WC cubicle actually is.
   */
  width: 1.05,
  doorWidth: 0.8,
  partition: 0.12,
  /** Cubicle walls stop short of the barn's ceiling, the way they really do. */
  height: 2.4,
  /** The back of the block is the navetta's own west wall. */
  backX: 126.0,
} as const;

/** The far edge of the last cubicle. Everything at the junction is past it. */
const WC_END_Z = WC.firstZ - WC.width * WC.count;

/**
 * The junction the corridor ends in, and the three ways out of it.
 *
 * Right is the juhlasali, and it is deliberately the *far* end of the walk:
 * a hall you turn into two metres inside the front door makes the rest of the
 * corridor scenery. Left is the keittiö and straight on is the karaoke, which
 * are the two rooms a venue signposts from exactly here.
 *
 * The kitchen keeps its door shut. It is one of the sealed back rooms, so an
 * opening there would be an opening into nothing - a closed leaf is both the
 * honest thing and what a kitchen door is doing at nine in the morning. The
 * karaoke is a real doorway with a real stub of corridor behind it, because
 * "eteenpäin" has to lead somewhere or the arrow is a lie.
 */
export const RISTEYS = {
  /** Into the hall: 2.4 m of it, starting where the third cubicle ends. */
  saliFrom: WC_END_Z,
  saliTo: WC_END_Z - 2.4,
  /**
   * The kitchen door, on the wall opposite - and past the hall opening rather
   * than across from it. It begins where `saliTo` ends, so the junction does
   * not present two doorways facing each other, and the leaf lands in the
   * middle of the blank stretch between the turn and the karaoke instead of
   * up against the last WC.
   */
  keittioFrom: WC_END_Z - 2.4,
  keittioTo: WC_END_Z - 3.3,
  /** Straight on: the doorway in the end wall, centred in the corridor. */
  karaokeMinX: 129.4,
  karaokeMaxX: 131.0,
  /** And the length of corridor behind it, before the karaoke's own door. */
  karaokeEndZ: ETEINEN.backZ - 2.05,
  /** Head height for both openings, so the signs have something to sit on. */
  lintel: 2.4,
} as const;

/** Where cubicle `index` sits, and where its doorway is cut. */
export function stall(index: number) {
  const from = WC.firstZ - WC.width * index;
  const center = from - WC.width / 2;
  return {
    from,
    to: from - WC.width,
    center,
    door: { minZ: center - WC.doorWidth / 2, maxZ: center + WC.doorWidth / 2 },
  };
}

export const STALLS = Array.from({ length: WC.count }, (_, index) => stall(index));

/** The corridor's left-hand wall, cut into segments by the three doorways. */
export function eteisenLansiseina(): SolidSpec[] {
  const edges = [ETEINEN.frontZ, ...STALLS.flatMap((s) => [s.door.maxZ, s.door.minZ]), ETEINEN.backZ];
  const walls: SolidSpec[] = [];
  for (let i = 0; i < edges.length; i += 2) {
    walls.push(
      solid(
        `eteinen-lansi-${i / 2 + 1}`,
        'lanWall',
        ETEINEN.westX,
        Math.min(edges[i], edges[i + 1]),
        ETEINEN.westX + 0.35,
        Math.max(edges[i], edges[i + 1]),
        BARN_H,
      ),
    );
  }
  return walls;
}

/** The partitions between the cubicles, plus the two that close the block. */
export function wcValiseinat(): SolidSpec[] {
  return Array.from({ length: WC.count + 1 }, (_, index) => {
    const z = WC.firstZ - WC.width * index;
    // Stops *inside* the corridor wall rather than flush with its face: two
    // boxes that end on the same plane z-fight along the whole seam.
    return solid(
      `wc-valiseina-${index + 1}`,
      // Lighter than the corridor: the cubicles are tiled, and it is what makes
      // three dark holes in a wall read as three WCs.
      'barnWall',
      WC.backX,
      z - WC.partition / 2,
      ETEINEN.westX + 0.15,
      z + WC.partition / 2,
      WC.height,
    );
  });
}

/**
 * What is actually in a cubicle: a pönttö against the outer wall, and the door
 * standing open against the partition. Every door is open on purpose - a
 * closed one is a flat panel, and the point of the detail is that you can see
 * in as you go past.
 */
function wcSisustus(): { decor: DecorSpec[]; handles: HandleSpec[] } {
  const items: DecorSpec[] = [];
  const painikkeet: HandleSpec[] = [];
  STALLS.forEach((spec, index) => {
    const n = index + 1;
    // The leaf, swung back into the cubicle against the partition. Hinged on
    // the corridor end of itself, which is the jamb it was swung off.
    const leaf = decor(
      `wc-ovi-${n}`,
      'door',
      ETEINEN.westX - 0.03 - WC.doorWidth,
      spec.door.maxZ - 0.09,
      ETEINEN.westX - 0.03,
      spec.door.maxZ - 0.03,
      2.05,
    );
    // Nothing here shares a face with anything else: coplanar decor z-fights,
    // and a flickering toilet is exactly the kind of thing you notice.
    items.push(
      decor(`wc-pontto-${n}`, 'porcelain', 126.4, spec.center - 0.25, 126.85, spec.center + 0.25, 0.42),
      decor(`wc-vesisailio-${n}`, 'porcelain', 126.02, spec.center - 0.22, 126.44, spec.center + 0.22, 0.44, 0.42),
      decor(`wc-kansi-${n}`, 'lanWall', 126.38, spec.center - 0.26, 126.9, spec.center + 0.26, 0.07, 0.4),
      leaf,
    );
    // A painike on each face, because both of them are in open air on a door
    // standing open: you see the back of it through the doorway on the way up
    // the corridor and the front of it looking into the cubicle. The nine
    // centimetres between the leaf and the partition is what the outer one has
    // to fit in, and it does with a centimetre to spare.
    painikkeet.push(
      handle(`wc-ovi-${n}-painike`, leaf.box, 1, 1),
      handle(`wc-ovi-${n}-painike-sisa`, leaf.box, 1, -1),
    );
  });
  return { decor: items, handles: painikkeet };
}

export const WC_SISUSTUS = wcSisustus();

/* ------------------------------------------------------- risteyksen ovet -- */

/**
 * What the junction looks like: a shut door to the kitchen, and a head over
 * each of the two openings.
 *
 * All of it is decor. The kitchen door is a leaf hung on a wall that stays
 * solid, so it can never be walked through into the void the keittiö block is
 * modelled as; the lintels are soffits filling the top of an opening that is
 * cut full height, which is what gives the signs above them something to be
 * mounted on instead of hanging in mid-air.
 */
function risteyksenOvet(): { decor: DecorSpec[]; handles: HandleSpec[] } {
  const face = ETEINEN.westX + 0.35;
  // The leaf, proud of the wall by the thickness of its own frame. Hinged at
  // the `keittioTo` end, so it opens away from the turn into the hall.
  const keittionOvi = decor(
    'keittion-ovi',
    'door',
    face + 0.01,
    RISTEYS.keittioTo,
    face + 0.05,
    RISTEYS.keittioFrom,
    2.05,
  );
  // The karaoke's own door, shut, at the end of the stub behind it. Hinged on
  // the west jamb, which puts its painike on the side you come round.
  const karaokenOvi = decor(
    'karaoken-ovi',
    'door',
    129.45,
    RISTEYS.karaokeEndZ + 0.01,
    130.95,
    RISTEYS.karaokeEndZ + 0.06,
    2.05,
  );
  const items = [
    keittionOvi,
    // Karmi: two jambs and a head, standing a little further out again.
    decor('keittion-karmi-a', 'furniture', face + 0.01, RISTEYS.keittioFrom, face + 0.09, RISTEYS.keittioFrom + 0.07, 2.13),
    decor('keittion-karmi-b', 'furniture', face + 0.01, RISTEYS.keittioTo - 0.07, face + 0.09, RISTEYS.keittioTo, 2.13),
    decor('keittion-karmi-yla', 'furniture', face + 0.01, RISTEYS.keittioTo - 0.07, face + 0.09, RISTEYS.keittioFrom + 0.07, 0.08, 2.05),

    // Head over the hall opening, and over the one straight ahead.
    decor(
      'sali-aukon-kamana',
      'lanWall',
      131.2,
      RISTEYS.saliTo,
      131.55,
      RISTEYS.saliFrom,
      BARN_H - RISTEYS.lintel,
      RISTEYS.lintel,
    ),
    decor(
      'karaoke-aukon-kamana',
      'lanWall',
      RISTEYS.karaokeMinX,
      ETEINEN.backZ - 0.35,
      RISTEYS.karaokeMaxX,
      ETEINEN.backZ,
      BARN_H - RISTEYS.lintel,
      RISTEYS.lintel,
    ),
    karaokenOvi,
  ];
  // One painike each, on the face that is in the room. Both of these are
  // leaves hung on a solid wall, so the other side of them is inside a block
  // the level does not model - a handle screwed to it would be ironmongery in
  // the masonry.
  return {
    decor: items,
    handles: [
      handle('keittion-painike', keittionOvi.box, -1, 1),
      handle('karaoken-painike', karaokenOvi.box, -1, 1),
    ],
  };
}

export const RISTEYKSEN_OVET = risteyksenOvet();
